import conf from '@/shared/config/useConfig';
import { HttpService } from '@/shared/api/services/HttpService.ts';
import { buildUrlForServiceName } from '@/connection/url-retriever.ts';
import type {
  Buergschaftsauftrag,
  BuergschaftsauftragDetailsFuerAufgabenListe,
} from '@/types/Buergschaftsauftrag.ts';
import type {
  BuergschaftsauftragFilter,
  BuergschaftsauftragFilterResponse,
} from '@/connection/filter-helper/filter-helper-buergschaftsauftraege.ts';
import { configForDownload, configForJsonPostDownload } from '@/connection/base.ts';
import type { FilterOptionen } from '@/components/datatable/types/FilterObject.ts';

export class SharedBuergschaftsauftragService extends HttpService {
  protected baseUrl = `${buildUrlForServiceName(conf.bürgschaftserviceName)}/buergschaftservice/api/private/v2/buergschaftsauftraege`;

  async holeBuergschaftsauftragDetailsFuerAufgabenListe(
    id: string,
  ): Promise<BuergschaftsauftragDetailsFuerAufgabenListe> {
    return this.httpClient.get(`${this.baseUrl}/${id}/aufgabendetails`);
  }

  async holeBuergschaftsauftragDetails(id: number): Promise<Buergschaftsauftrag> {
    return this.httpClient.get(`${this.baseUrl}/${id}`);
  }

  async holeAlleBuergschaftsauftraegeCsv(
    buergschaftsauftragFilter: BuergschaftsauftragFilter,
  ): Promise<BlobPart> {
    /**
     * AG: Die Aufruferrolle wird in der Domäne AG mitgegeben, um zu unterscheiden, ob die
     *         ausgestellten oder eingegangene Bürgschaften exportiert werden sollen
     * TL, BÜ: in den anderen beiden Domänen werden immer alle Bürgschaften exportiert
     */
    return this.httpClient.post(`${this.baseUrl}/filterung/buergschaftsauftraege.csv`, {
      data: buergschaftsauftragFilter,
      config: configForJsonPostDownload,
    });
  }

  async holeBuergschaftsauftragCsv(auftragId: number): Promise<BlobPart> {
    return this.httpClient.get(`${this.baseUrl}/${auftragId}/buergschaftsauftraege.csv`, {
      config: configForDownload,
    });
  }

  async holeFilterOptionenBuergschaftsauftraege(
    buergschaftsauftragFilter: BuergschaftsauftragFilter,
  ): Promise<FilterOptionen> {
    return this.httpClient.post(`${this.baseUrl}/filteroptionen`, {
      data: {
        kautionsvertragIds: buergschaftsauftragFilter?.kautionsvertragIds,
        buergeIds: buergschaftsauftragFilter?.buergen,
      },
    });
  }

  async holeBuergschaftsauftraegeMitPaginierungUndFilterung(
    seite: number,
    seitengroesse: number,
    property: string,
    sortierArt: string,
    buergschaftsauftragFilter: BuergschaftsauftragFilter,
  ): Promise<BuergschaftsauftragFilterResponse> {
    return this.httpClient.post(`${this.baseUrl}/filterung`, {
      params: { page: seite, size: seitengroesse, sort: `${property},${sortierArt}` },
      data: buergschaftsauftragFilter,
    });
  }

  async downloadAntragsdaten(auftragId: number): Promise<BlobPart> {
    return this.httpClient.get(`${this.baseUrl}/${auftragId}/daten.zip`, {
      config: configForDownload,
    });
  }
}
